<template>
    <div>
        <v-card v-if="isQr" outlined class="mb-3">
            <v-card-title class="px-3 py-2">
                <v-icon left small color="primary">mdi-qrcode</v-icon>
                <span class="text-subtitle-1 font-weight-medium">QR Code da stampare</span>
                <v-spacer></v-spacer>
                <v-btn small text color="primary" @click="downloadPng">
                    <v-icon left small>mdi-download</v-icon>
                    PNG
                </v-btn>
                <v-btn small text color="primary" @click="downloadSvg">
                    <v-icon left small>mdi-svg</v-icon>
                    SVG
                </v-btn>
                <v-btn small text color="primary" @click="printPdf">
                    <v-icon left small>mdi-printer</v-icon>
                    Stampa
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="d-flex align-center" style="gap:16px">
                <div ref="qrPreview" class="qr-preview"></div>
                <div>
                    <div class="text-caption grey--text">Codice tag</div>
                    <div class="text-body-1 font-weight-bold">{{ device.code }}</div>
                    <div class="text-caption grey--text mt-2">URL deep-link</div>
                    <div class="text-body-2" style="word-break:break-all">{{ deepLinkUrl }}</div>
                    <div class="text-caption grey--text mt-2">
                        Stampa il QR e fissalo all'ingresso del luogo di lavoro. Quando un dipendente lo inquadra, l'app apre direttamente il widget timbratura precompilato col codice del dispositivo.
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <v-card v-if="isNfc" outlined class="mb-3">
            <v-card-title class="px-3 py-2">
                <v-icon left small color="primary">mdi-nfc-variant</v-icon>
                <span class="text-subtitle-1 font-weight-medium">Scrittura tag NFC</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
                <v-alert v-if="nfcSupported" type="success" dense text>
                    Il tuo browser supporta la scrittura NFC. Tieni il tag NFC vicino al telefono e premi "Scrivi tag" — bastano pochi secondi.
                </v-alert>
                <v-alert v-else-if="nfcUnsupportedReason === 'http'" type="warning" dense text>
                    Web NFC richiede una connessione <strong>HTTPS</strong>. Stai aprendo l'app via HTTP, quindi il browser ha disabilitato la scrittura NFC.
                    Apri l'app dal dominio HTTPS (es. <code>app.givemehive.cloud</code>), oppure usa l'app gratuita <a href="https://play.google.com/store/apps/details?id=com.wakdev.wdnfc" target="_blank">NFC Tools</a> per scrivere manualmente il payload sotto.
                </v-alert>
                <v-alert v-else-if="nfcUnsupportedReason === 'browser'" type="info" dense text>
                    Web NFC e' supportato solo su <strong>Chrome / Edge per Android</strong> (no iOS, no desktop).
                    Apri questa pagina da Android, oppure usa l'app gratuita <a href="https://play.google.com/store/apps/details?id=com.wakdev.wdnfc" target="_blank">NFC Tools</a> e scrivi manualmente il payload sotto.
                </v-alert>
                <v-alert v-else type="info" dense text>
                    Web NFC non disponibile in questo browser. Usa l'app gratuita <a href="https://play.google.com/store/apps/details?id=com.wakdev.wdnfc" target="_blank">NFC Tools</a> per scrivere il payload sotto.
                </v-alert>

                <div class="mt-3 mb-3">
                    <div class="text-caption grey--text">Payload da scrivere sul tag (URL)</div>
                    <div class="d-flex align-center" style="gap:8px">
                        <code style="flex:1; padding:6px 10px; background:#f5f5f5; border-radius:4px; word-break:break-all">{{ deepLinkUrl }}</code>
                        <v-btn icon small @click="copyPayload"><v-icon small>mdi-content-copy</v-icon></v-btn>
                    </div>
                </div>

                <v-btn v-if="nfcSupported" color="primary" :loading="writing" @click="writeNfcTag">
                    <v-icon left small>mdi-nfc-tap</v-icon>
                    Scrivi tag NFC
                </v-btn>

                <v-alert v-if="nfcMessage" :type="nfcMessageType" dense text class="mt-3 mb-0">
                    {{ nfcMessage }}
                </v-alert>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import QRCode from 'qrcode'

export default {
    name: 'hr-clock-device-helper',

    props: {
        device: { type: Object, required: true },
    },

    data() {
        return {
            writing: false,
            nfcMessage: null,
            nfcMessageType: 'info',
        }
    },

    computed: {
        isQr() {
            const t = (this.device && this.device.type ? String(this.device.type) : '').toLowerCase()
            return t.includes('qr')
        },
        isNfc() {
            const t = (this.device && this.device.type ? String(this.device.type) : '').toLowerCase()
            return t.includes('nfc')
        },
        deepLinkUrl() {
            if (!this.device || !this.device.code) return ''
            // In produzione sempre app.givemehive.cloud, l'unico dominio dichiarato
            // dall'app: un QR generato da <tenant>.givemehive.cloud non la aprirebbe.
            // In locale resta l'origin, cosi' un QR di prova non timbra in produzione.
            const origin = process.env.VUE_APP_DEEP_LINK_ORIGIN || window.location.origin
            // Senza '#': il frammento non viene inviato al server e non compare
            // nel percorso, quindi ne' Android ne' iOS possono riconoscere il
            // link come appartenente all'app. Chi l'app non ce l'ha ci arriva lo
            // stesso: nginx redirige /timbra/<codice> sulla rotta hash del
            // gestionale (gmhdock, images/nginx/snippets/timbra.conf).
            return `${origin}/timbra/${encodeURIComponent(this.device.code)}`
        },
        nfcSupported() {
            if (typeof window === 'undefined') return false
            return 'NDEFReader' in window && window.isSecureContext
        },
        isAndroidChrome() {
            if (typeof navigator === 'undefined') return false
            const ua = navigator.userAgent || ''
            return /Android/i.test(ua) && /Chrome|CriOS/i.test(ua)
        },
        nfcUnsupportedReason() {
            if (this.nfcSupported) return null
            if (typeof window === 'undefined') return 'unknown'
            if (!('NDEFReader' in window)) {
                if (this.isAndroidChrome) return 'no-api'
                return 'browser'
            }
            if (!window.isSecureContext) return 'http'
            return 'unknown'
        },
    },

    watch: {
        deepLinkUrl: 'renderQr',
        isQr() { this.$nextTick(() => this.renderQr()) },
    },

    mounted() {
        this.renderQr()
    },

    methods: {
        renderQr() {
            if (!this.isQr || !this.deepLinkUrl) return
            this.$nextTick(() => {
                const container = this.$refs.qrPreview
                if (!container) return
                QRCode.toCanvas(this.deepLinkUrl, {
                    width: 220,
                    margin: 2,
                    errorCorrectionLevel: 'M',
                }, (err, canvas) => {
                    if (err) return
                    container.innerHTML = ''
                    container.appendChild(canvas)
                })
            })
        },
        downloadPng() {
            QRCode.toDataURL(this.deepLinkUrl, { width: 1200, margin: 4, errorCorrectionLevel: 'H' }, (err, url) => {
                if (err) return
                const a = document.createElement('a')
                a.href = url
                a.download = `qr-${this.device.code}.png`
                document.body.appendChild(a); a.click(); a.remove()
            })
        },
        downloadSvg() {
            QRCode.toString(this.deepLinkUrl, { type: 'svg', margin: 4, errorCorrectionLevel: 'H' }, (err, svg) => {
                if (err) return
                const blob = new Blob([svg], { type: 'image/svg+xml' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `qr-${this.device.code}.svg`
                document.body.appendChild(a); a.click(); a.remove()
                setTimeout(() => URL.revokeObjectURL(url), 1000)
            })
        },
        printPdf() {
            // Stampa via window.print con un layout dedicato A6 con QR + label.
            QRCode.toDataURL(this.deepLinkUrl, { width: 1200, margin: 4, errorCorrectionLevel: 'H' }, (err, url) => {
                if (err) return
                const w = window.open('', '_blank', 'width=600,height=600')
                if (!w) return
                const label = (this.device.label || this.device.code || '').replace(/</g, '&lt;')
                const workplace = (this.device.workplace_name || '').replace(/</g, '&lt;')
                w.document.write(`<!doctype html>
<html><head><title>QR ${this.device.code}</title>
<style>
  @page { size: A6 portrait; margin: 8mm; }
  body { font-family: system-ui, sans-serif; text-align: center; }
  .label { font-size: 11pt; color:#666; margin-top:6mm; }
  .name { font-size: 16pt; font-weight: 700; margin-top:2mm; }
  .code { font-size: 10pt; color:#999; margin-top:4mm; letter-spacing:1px; }
  img { width: 80mm; height: auto; }
</style></head>
<body>
  <img src="${url}" alt="QR">
  <div class="name">${label}</div>
  <div class="label">${workplace}</div>
  <div class="code">${this.device.code}</div>
  <script>window.onload = () => { window.print(); window.onafterprint = () => window.close(); };<\/script>
</body></html>`)
                w.document.close()
            })
        },
        async copyPayload() {
            try {
                await navigator.clipboard.writeText(this.deepLinkUrl)
                this.nfcMessage = 'URL copiato negli appunti'
                this.nfcMessageType = 'success'
            } catch {
                this.nfcMessage = 'Impossibile copiare automaticamente'
                this.nfcMessageType = 'warning'
            }
        },
        async writeNfcTag() {
            if (!this.nfcSupported) return
            this.writing = true
            this.nfcMessage = 'Avvicina il tag NFC al telefono...'
            this.nfcMessageType = 'info'
            try {
                const ndef = new window.NDEFReader()
                await ndef.write({ records: [{ recordType: 'url', data: this.deepLinkUrl }] })
                this.nfcMessage = 'Tag NFC scritto con successo!'
                this.nfcMessageType = 'success'
            } catch (e) {
                this.nfcMessage = 'Errore: ' + (e.message || e)
                this.nfcMessageType = 'error'
            } finally {
                this.writing = false
            }
        },
    },
}
</script>

<style scoped>
.qr-preview {
    display: inline-block;
    padding: 8px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
}
</style>
